import { useEffect, useMemo, useState } from "react";
import api, { rupiah, shortDate } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { Wallet, FileText, Ticket, AlertTriangle, Clock, RefreshCw, TrendingUp, Plane, CheckCircle2, Percent, ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const Stat = ({ icon: Icon, label, value, tint = "teal", tid }) => (
  <div className="stat-card" data-testid={tid}>
    <div className="flex items-start justify-between gap-2">
      <div className="min-w-0 flex-1">
        <div className="text-[10px] sm:text-[11px] uppercase tracking-wider text-slate-500 font-semibold leading-tight">{label}</div>
        <div className="mt-1.5 sm:mt-2 text-lg sm:text-2xl font-extrabold text-slate-900 mono whitespace-nowrap overflow-hidden text-ellipsis leading-tight">{value}</div>
      </div>
      <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-xl flex items-center justify-center shrink-0 ${tint === "teal" ? "bg-[#f0f2e6] text-[#556b2f]" : tint === "amber" ? "bg-amber-50 text-amber-700" : "bg-slate-100 text-slate-700"}`}>
        <Icon size={18} />
      </div>
    </div>
  </div>
);

const ID_MONTHS = ["Januari","Februari","Maret","April","Mei","Juni","Juli","Agustus","September","Oktober","November","Desember"];
const monthLabel = (ym) => {
  if (!ym) return "";
  const [y, m] = ym.split("-").map(Number);
  return `${ID_MONTHS[m-1] || m} ${y}`;
};

export default function Dashboard() {
  const [d, setD] = useState(null);
  const [month, setMonth] = useState(""); // "" = current month

  useEffect(() => {
    api.get("/dashboard", { params: month ? { month } : {} }).then((r) => setD(r.data)).catch(() => {});
  }, [month]);

  const upcomingByDay = useMemo(() => {
    if (!d?.upcoming) return [];
    const groups = new Map();
    for (const f of d.upcoming) {
      const key = f.depart_date || (f.depart || "").slice(0, 10);
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(f);
    }
    return [...groups.entries()].sort(([a],[b]) => a.localeCompare(b));
  }, [d]);

  if (!d) return <div className="text-slate-400">Memuat dashboard...</div>;
  const viewingCurrent = d.viewing_current !== false;

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-[#556b2f] font-bold">Gogojalan Dashboard</div>
          <h1 className="text-2xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-slate-900 mt-1">
            {viewingCurrent ? "Selamat datang kembali" : `Data ${monthLabel(d.month)}`}
          </h1>
          <p className="text-slate-500 mt-2 text-sm sm:text-lg">Ringkasan operasional harian agen travel Anda.</p>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <Select value={month || d.month} onValueChange={(v) => setMonth(v === (new Date().toISOString().slice(0,7)) ? "" : v)}>
            <SelectTrigger className="w-[190px] bg-white" data-testid="dashboard-month-picker">
              <SelectValue placeholder="Pilih bulan" />
            </SelectTrigger>
            <SelectContent>
              {(d.months_available || [d.month]).map((m) => (
                <SelectItem key={m} value={m}>{monthLabel(m)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Link to="/bookings/new"><Button data-testid="quick-retrieve" className="bg-[#556b2f] hover:bg-[#3d4d1e]">Retrieve Booking</Button></Link>
          <Link to="/invoices/new"><Button data-testid="quick-invoice" variant="outline">+ Invoice</Button></Link>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
        <Stat tid="stat-today-sales" icon={Wallet} label={viewingCurrent ? "Penjualan Hari Ini" : "Penjualan Hari Ini (—)"} value={rupiah(d.today_sales)} />
        <Stat tid="stat-month-sales" icon={TrendingUp} label={`Penjualan ${monthLabel(d.month)}`} value={rupiah(d.month_sales)} />
        <Stat tid="stat-month-margin" icon={Percent} label={`Net Margin ${monthLabel(d.month)}`} value={rupiah(d.month_gross_margin || 0)} />
        <Stat tid="stat-month-servicefee" icon={FileText} label={`Service Fee ${monthLabel(d.month)}`} value={rupiah(d.month_service_fee || 0)} />
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
        <Stat tid="stat-invoice-today" icon={FileText} label="Invoice Hari Ini" value={d.invoices_today} />
        <Stat tid="stat-tickets-today" icon={Ticket} label="Tiket Terbit Hari Ini" value={d.tickets_today} />
        <Stat tid="stat-pending-payment" icon={Clock} label="Menunggu Bayar" value={d.pending_payment} tint="amber" />
        <Stat tid="stat-outstanding" icon={Wallet} label="Outstanding Invoice" value={rupiah(d.outstanding)} tint="amber" />
      </div>

      {/* Upcoming (grouped per day, clickable) & Completed */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-slate-900 flex items-center justify-between">
              <span className="flex items-center gap-2"><Plane size={18} className="text-[#556b2f]"/> Penerbangan Mendatang</span>
              <span className="text-xs font-medium text-slate-500">{(d.upcoming || []).length} penerbangan</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {(d.upcoming || []).length === 0 && <p className="text-sm text-slate-400">Tidak ada penerbangan mendatang.</p>}
            <div className="max-h-[520px] overflow-y-auto pr-1 -mr-1 space-y-4" data-testid="upcoming-list">
              {upcomingByDay.map(([day, list]) => (
                <div key={day}>
                  <div className="text-[11px] uppercase tracking-wider text-[#556b2f] font-bold mb-2 sticky top-0 bg-white/95 backdrop-blur py-1">
                    {shortDate(day)} · {list.length} flight{list.length > 1 ? "s" : ""}
                  </div>
                  <ul className="space-y-2">
                    {list.map((f) => (
                      <li key={f.id + f.depart}>
                        <Link
                          to={`/etickets/${f.id}`}
                          className="flex items-center gap-3 p-2 rounded-lg hover:bg-[#f0f2e6]/50 border border-slate-100 hover:border-[#556b2f]/40 transition-colors"
                          data-testid={`upcoming-${f.eticket_number}`}
                        >
                          <div className="w-10 h-10 rounded-lg bg-[#f0f2e6] text-[#556b2f] flex items-center justify-center shrink-0">
                            <Plane size={16}/>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-semibold text-slate-900 truncate">{f.passenger || "-"}</div>
                            <div className="text-xs text-slate-500 truncate">{f.airline || "-"} · {(f.from || "").split("(")[0]?.trim()} → {(f.to || "").split("(")[0]?.trim()}</div>
                            <div className="text-xs text-[#556b2f] font-medium mt-0.5 mono">{f.depart} · PNR {f.pnr || "-"}</div>
                          </div>
                          <ChevronRight size={16} className="text-slate-300 shrink-0" />
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="text-slate-900 flex items-center gap-2"><CheckCircle2 size={18} className="text-slate-500"/> Penerbangan Selesai</CardTitle></CardHeader>
          <CardContent>
            {(d.completed || []).length === 0 && <p className="text-sm text-slate-400">Belum ada penerbangan selesai.</p>}
            <ul className="space-y-3" data-testid="completed-list">
              {(d.completed || []).map((f, i) => (
                <li key={i}>
                  <Link to={`/etickets/${f.id}`} className="flex items-start gap-3 p-2 rounded-lg hover:bg-slate-50 border border-slate-100 transition-colors">
                    <div className="w-10 h-10 rounded-lg bg-slate-100 text-slate-500 flex items-center justify-center shrink-0">
                      <CheckCircle2 size={16}/>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-slate-700 truncate">{f.passenger || "-"}</div>
                      <div className="text-xs text-slate-500 truncate">{f.airline || "-"} · {(f.from || "").split("(")[0]?.trim()} → {(f.to || "").split("(")[0]?.trim()}</div>
                      <div className="text-xs text-slate-400 mt-0.5 mono">{f.depart}</div>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Stat tid="stat-need-ticket" icon={AlertTriangle} label="Perlu Terbitkan Tiket" value={d.need_ticket} tint="amber" />
        <Stat tid="stat-refund" icon={RefreshCw} label="Proses Refund" value={d.refund_process} tint="amber" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2">
          <CardHeader><CardTitle className="text-slate-900">Penjualan 7 Hari Terakhir</CardTitle></CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={d.chart}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="date" stroke="#64748b" fontSize={12} />
                  <YAxis stroke="#64748b" fontSize={12} tickFormatter={(v) => (v > 999999 ? (v / 1e6).toFixed(1) + "M" : v)} />
                  <Tooltip formatter={(v) => rupiah(v)} />
                  <Line type="monotone" dataKey="sales" stroke="#556b2f" strokeWidth={3} dot={{ r: 4, fill: "#6b8e23" }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="text-slate-900">Aktivitas Terbaru</CardTitle></CardHeader>
          <CardContent>
            {d.recent.length === 0 && <p className="text-sm text-slate-400">Belum ada aktivitas.</p>}
            <ul className="space-y-3" data-testid="recent-activity">
              {d.recent.map((a, i) => (
                <li key={i} className="flex items-center gap-3 text-sm">
                  <span className={`w-2 h-2 rounded-full ${a.kind === "invoice" ? "bg-[#6b8e23]" : "bg-indigo-500"}`}></span>
                  <span className="text-slate-700">{a.label}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
